/** Restore a cloud-backed project onto disk and open it. */

import { invoke } from "@tauri-apps/api/core";
import { authorizedFetch, hasCloudBackupEntitlement } from "./cloudAuth";
import { openProjectPath, pickProjectParentFolder } from "./tauri/project";
import { isTauri } from "./runtime";
import {
  downloadSnapshotPack,
  listSnapshots,
  snapshotApplyChain,
} from "./cloudSnapshot";
import { atIndexMaybe, isNothing } from "./maybe";
import { trimmedOrEmpty, trimmedOrNull } from "./trimmedString";

export type CloudProjectSummary = {
  id: string;
  name: string;
  clientProjectId: string | null;
  lastBackedUpAt: string | null;
};

type ApiProject = {
  id: string;
  name: string;
  client_project_id?: string | null;
  clientProjectId?: string | null;
  last_backed_up_at?: string | null;
  lastBackedUpAt?: string | null;
};

export async function canRestoreFromCloud(): Promise<boolean> {
  return hasCloudBackupEntitlement();
}

export type RestorableCloudList = {
  projects: CloudProjectSummary[];
  /** Backed-up cloud projects skipped because they appear in Recents. */
  alreadyLocalCount: number;
};

/** Projects that have a backup and are not already known locally. */
export async function listRestorableCloudProjects(): Promise<RestorableCloudList> {
  const res = await authorizedFetch("/v1/projects");
  if (res.ok === false) {
    throw new Error(`Could not list cloud projects (${res.status})`);
  }
  const body = (await res.json()) as { projects?: ApiProject[] };

  let localIds = new Set<string>();
  if (isTauri()) {
    try {
      const ids = await invoke<string[]>("list_known_local_cloud_project_ids");
      localIds = new Set(ids.filter((id) => id.length > 0));
    } catch (error) {
      console.warn("[cloudRestore] could not read recent cloud ids", error);
    }
  }

  const backedUp = (body.projects ?? [])
    .map((p) => {
      const name = trimmedOrEmpty(p.name);
      return {
        id: p.id,
        name: name.length > 0 ? name : "Untitled",
        clientProjectId: trimmedOrNull(
          p.client_project_id ?? p.clientProjectId,
        ),
        lastBackedUpAt: p.last_backed_up_at ?? p.lastBackedUpAt ?? null,
      };
    })
    .filter((p) => p.lastBackedUpAt !== null && p.lastBackedUpAt.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name));

  const projects = backedUp.filter(
    (p) =>
      p.clientProjectId === null ||
      p.clientProjectId.length === 0 ||
      localIds.has(p.clientProjectId) === false,
  );
  const alreadyLocalCount = backedUp.length - projects.length;

  return { projects, alreadyLocalCount };
}

type RestorePack = {
  bundleBase64: string;
  gitHeadOid: string;
  chronicleHeadOid: string | null;
};

async function downloadRestoreChain(
  cloudProjectId: string,
): Promise<RestorePack[]> {
  const listed = await listSnapshots(cloudProjectId);
  if (listed.length === 0) {
    throw new Error("No backup found for that project");
  }
  const chain = snapshotApplyChain(listed);
  const packs: RestorePack[] = [];
  for (const snap of chain) {
    const pack = await downloadSnapshotPack(cloudProjectId, snap.id);
    const tip = trimmedOrNull(pack.gitHeadOid ?? snap.gitHeadOid);
    if (tip === null) throw new Error("Restore response missing tip commit id");
    packs.push({
      bundleBase64: pack.bundleBase64,
      gitHeadOid: tip,
      chronicleHeadOid: pack.chronicleHeadOid,
    });
  }
  return packs;
}

/**
 * Download the latest cloud snapshot into a new local folder and open it.
 * Returns false if the user cancels the folder picker (desktop only).
 */
export async function restoreCloudProjectToDisk(
  project: CloudProjectSummary,
  replaceSplash = true,
): Promise<boolean> {
  if (isTauri() === false)
    throw new Error("Use the web editor to open cloud projects");

  const parent = await pickProjectParentFolder(
    "Choose where to save the restored project",
  );
  if (parent === null || parent.length === 0) return false;

  const packs = await downloadRestoreChain(project.id);
  if (packs.length === 0) {
    throw new Error("No backup found for that project");
  }
  const firstM = atIndexMaybe(packs, 0);
  const lastM = atIndexMaybe(packs, packs.length - 1);
  if (isNothing(firstM) || isNothing(lastM)) {
    throw new Error("No backup found for that project");
  }
  const first = firstM.value;
  const last = lastM.value;
  const openAfter = packs.length === 1;
  const root = await invoke<string>("restore_project_from_bundle", {
    parentPath: parent,
    folderName: project.name,
    bundleBase64: first.bundleBase64,
    gitHeadOid: first.gitHeadOid,
    cloudClientProjectId: project.clientProjectId,
    replaceSplash: openAfter ? replaceSplash : false,
    openAfter,
    chronicleHeadOid: first.chronicleHeadOid,
  });

  for (const pack of packs.slice(1)) {
    await invoke("apply_cloud_bundle", {
      rootPath: root,
      bundleBase64: pack.bundleBase64,
      gitHeadOid: pack.gitHeadOid,
      resolution: null,
    });
  }

  // The chronicle (#105 Phase 1) only needs the *final* pack's state, not
  // mid-chain replay — `restore_project_from_bundle` above already applied
  // it from `first` when that was also the last (and only) pack.
  if (
    last !== first &&
    last.chronicleHeadOid !== null &&
    last.chronicleHeadOid.length > 0
  ) {
    await invoke("apply_chronicle_bundle", {
      rootPath: root,
      bundleBase64: last.bundleBase64,
      chronicleHeadOid: last.chronicleHeadOid,
    });
  }

  if (openAfter === false) {
    const tip = last.gitHeadOid;
    await invoke("patch_cloud_sync", {
      rootPath: root,
      lastUploadedOid: tip,
      lastPulledOid: tip,
      clearDivergence: true,
      lastUploadedChronicleOid: last.chronicleHeadOid,
      lastPulledChronicleOid: last.chronicleHeadOid,
    });
    await openProjectPath(root, replaceSplash);
  }

  return true;
}
